import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Network, Megaphone, Users, TrendingUp, Wrench, GraduationCap, ShieldCheck,
  ArrowRight, ArrowDown, ChevronRight, Check, X, MapPin, Calendar, Linkedin,
  Building2, Factory, Truck, Sparkles, Route, LineChart, Globe2,
} from "lucide-react";
import EuropeMap from "../components/EuropeMap";
import { Counter, SectionHeader, PillLabel } from "../components/UI";

const HERO_IMG = "https://images.pexels.com/photos/19233057/pexels-photo-19233057.jpeg";
const CAP_IMG = "https://images.unsplash.com/photo-1581091212911-f4efc3f71c48";

const WhyCards = () => {
  const { t } = useTranslation();
  const cards = [
    { key: "distribution", Icon: Network },
    { key: "marketing", Icon: Megaphone },
    { key: "dealer", Icon: Users },
    { key: "sales", Icon: TrendingUp },
    { key: "after", Icon: Wrench },
    { key: "training", Icon: GraduationCap },
    { key: "regulatory", Icon: ShieldCheck },
  ];
  return (
    <section id="why-eema" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeader eyebrow={t("why.eyebrow")} title={t("why.title")} subtitle={t("why.subtitle")} />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map(({ key, Icon }, idx) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: idx * 0.06 }}
              data-testid={`why-card-${key}`}
              className={`premium-card p-8 ${idx === 6 ? 'md:col-span-2 lg:col-span-1' : ''}`}
            >
              <div className="h-12 w-12 rounded-sm bg-[#003399]/5 flex items-center justify-center mb-6">
                <Icon size={22} strokeWidth={1.5} className="text-[#003399]" />
              </div>
              <h3 className="font-display font-semibold text-xl text-[#0B1120] mb-3">{t(`why.cards.${key}.title`)}</h3>
              <p className="text-[#4A5568] leading-relaxed text-sm">{t(`why.cards.${key}.desc`)}</p>
              <div className="mt-6 flex items-center text-[#003399] text-xs tracking-widest uppercase font-display font-medium">
                <span>Integrated</span>
                <ChevronRight size={14} strokeWidth={1.5} className="ml-1" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const MarketsSection = () => {
  const { t } = useTranslation();
  const [tip, setTip] = useState(null); // { id, x, y }
  const countryKeys = ["italy","germany","switzerland","netherlands","belgium","luxembourg","baltics","uk","portugal","greece","austria","poland","nordics","czech","hungary","romania","croatia"];
  const activeKeys = ["italy","germany","switzerland","netherlands","belgium","luxembourg","baltics"];
  const sidebarKeys = ["italy","germany","switzerland","netherlands","baltics"];

  const onEnter = (id, e) => setTip({ id, x: e.clientX, y: e.clientY });
  const onMove = (e) => setTip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : prev);
  const onLeave = () => setTip(null);

  const country = tip && countryKeys.includes(tip.id) ? t(`markets.countries.${tip.id}`, { returnObjects: true }) : null;
  const isActive = tip && activeKeys.includes(tip.id);

  return (
    <section id="markets" className="py-24 md:py-32 bg-[#F5F7FB] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeader eyebrow={t("markets.eyebrow")} title={<><span className="block">{t("markets.title_line1")}</span><span className="block text-[#003399]">{t("markets.title_line2")}</span></>} subtitle={t("markets.subtitle")} />
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8 bg-white p-8 rounded-sm border border-slate-200 relative">
            <EuropeMap onCountryEnter={onEnter} onCountryLeave={onLeave} onCountryMove={onMove} />
          </div>
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white p-6 rounded-sm border border-slate-200">
              <div className="text-xs tracking-[0.2em] uppercase text-[#4A5568] font-display mb-4">Legend</div>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-4 h-4 bg-[#003399] rounded-sm"></span>
                <span className="text-sm font-display font-medium text-[#0B1120]">{t("markets.legend_active")}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-4 h-4 bg-[#E5E9F2] rounded-sm border border-slate-300"></span>
                <span className="text-sm font-display font-medium text-[#0B1120]">{t("markets.legend_future")}</span>
              </div>
            </div>
            <div className="bg-[#003399] text-white p-6 rounded-sm">
              <div className="text-xs tracking-[0.2em] uppercase text-white/60 font-display mb-2">Founding Markets</div>
              <ul className="space-y-2 text-sm">
                {sidebarKeys.map(k => (
                  <li key={k} className="flex items-center justify-between border-b border-white/10 py-2 last:border-0">
                    <span className="font-display font-medium">{t(`markets.countries.${k}.name`)}</span>
                    <span className="text-white/70 text-xs">{t(`markets.countries.${k}.dealers`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {tip && country && (
        <div className="fixed pointer-events-none z-40" style={{ left: tip.x + 14, top: tip.y + 14 }}>
          <div className="bg-white border border-slate-200 shadow-2xl px-4 py-3 min-w-[220px] rounded-sm">
            <div className="font-display font-semibold text-[#003399] text-sm mb-2">{country.name}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">{t("markets.tooltip_partner")}</div>
            <div className="text-xs text-[#0B1120] mb-2">{country.partner}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">{t("markets.tooltip_dealers")}</div>
            <div className="text-xs text-[#0B1120] mb-2">{country.dealers}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">{t("markets.tooltip_status")}</div>
            <div className={`text-xs font-medium ${isActive ? 'text-[#003399]' : 'text-slate-500'}`}>
              {isActive ? t("markets.status_operational") : t("markets.status_expansion")}{country.note ? ` · ${country.note}` : ''}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const TimelineSection = () => {
  const { t } = useTranslation();
  const steps = t("build.steps", { returnObjects: true });
  const icons = [Factory, Building2, Megaphone, Users, TrendingUp, Wrench, Sparkles];
  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeader eyebrow={t("build.eyebrow")} title={t("build.title")} subtitle={t("build.subtitle")} align="center" />
        <div className="mt-20 relative">
          <div className="hidden md:block absolute top-8 left-0 right-0 h-[2px] timeline-line"></div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-6">
            {steps.map((label, i) => {
              const Icon = icons[i] || Sparkles;
              return (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="flex flex-col items-center text-center"
                  data-testid={`timeline-step-${i}`}
                >
                  <div className="h-16 w-16 rounded-full bg-white border-2 border-[#003399] flex items-center justify-center relative z-10">
                    <Icon size={22} strokeWidth={1.5} className="text-[#003399]" />
                  </div>
                  <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-[#4A5568] font-display">Step {i+1}</div>
                  <div className="mt-1 font-display font-semibold text-sm text-[#0B1120]">{label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const GoToMarketSection = () => {
  const { t } = useTranslation();
  const steps = t("gotomarket.steps", { returnObjects: true });
  return (
    <section className="py-24 md:py-32 bg-[#F5F7FB]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeader eyebrow={t("gotomarket.eyebrow")} title={t("gotomarket.title")} align="center" />
        <div className="mt-16 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
            {steps.map((s, i) => (
              <motion.div
                key={s}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, duration: 0.4 }}
                className="flex items-center gap-4 bg-white border border-slate-200 px-5 py-4 rounded-sm hover:border-[#003399] transition-colors"
                data-testid={`gtm-step-${i}`}
              >
                <span className="font-display font-bold text-[#00E5FF] text-2xl w-10">{String(i+1).padStart(2,'0')}</span>
                <span className="font-display font-medium text-[#0B1120] text-sm">{s}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CapabilitiesSection = () => {
  const { t } = useTranslation();
  const items = t("capabilities.items", { returnObjects: true });
  return (
    <section className="py-24 md:py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-5">
          <SectionHeader eyebrow={t("capabilities.eyebrow")} title={t("capabilities.title")} subtitle={t("capabilities.subtitle")} />
          <p className="mt-8 text-sm tracking-widest uppercase text-[#4A5568] font-display">{t("capabilities.footnote")}</p>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-8 lg:gap-12">
          {items.map((it, i) => (
            <Counter key={it.label} value={it.value} suffix={it.suffix} label={it.label} testId={`counter-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
};

const CompareSection = () => {
  const { t } = useTranslation();
  const without = t("compare.without", { returnObjects: true });
  const withE = t("compare.with", { returnObjects: true });
  return (
    <section className="py-24 md:py-32 bg-[#F5F7FB]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeader eyebrow={t("compare.eyebrow")} title={t("compare.title")} align="center" />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div initial={{opacity:0,x:-20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} className="bg-white border border-slate-200 p-10 rounded-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-10 w-10 rounded-sm bg-slate-100 flex items-center justify-center"><X size={18} className="text-slate-400" strokeWidth={1.5}/></div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-slate-400 font-display">Traditional</div>
                <h3 className="font-display font-semibold text-xl text-[#4A5568]">{t("compare.without_title")}</h3>
              </div>
            </div>
            <ul className="space-y-4">
              {without.map((w,i) => (
                <li key={i} className="flex items-start gap-3 text-[#4A5568]">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0"></span>
                  <span className="text-sm leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{opacity:0,x:20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} className="bg-[#003399] text-white p-10 rounded-sm relative overflow-hidden">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#00E5FF]/20 blur-2xl"></div>
            <div className="flex items-center gap-3 mb-8 relative">
              <div className="h-10 w-10 rounded-sm bg-[#00E5FF] flex items-center justify-center"><Check size={18} className="text-[#003399]" strokeWidth={2}/></div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#00E5FF] font-display">Alliance model</div>
                <h3 className="font-display font-semibold text-xl">{t("compare.with_title")}</h3>
              </div>
            </div>
            <ul className="space-y-4 relative">
              {withE.map((w,i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={16} className="text-[#00E5FF] mt-0.5 shrink-0" strokeWidth={2}/>
                  <span className="text-sm leading-relaxed text-white/95">{w}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const MembersSection = () => {
  const { t } = useTranslation();
  const profiles = t("members.profiles", { returnObjects: true });
  const openPositions = t("members.open_positions", { returnObjects: true });
  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeader eyebrow={t("members.eyebrow")} title={t("members.title")} subtitle={t("members.subtitle")} />
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {profiles.map((p, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.08}} className="premium-card overflow-hidden" data-testid={`member-card-${i}`}>
              <div className="h-40 bg-gradient-to-br from-[#003399] to-[#1a4dbd] relative">
                <div className="absolute inset-0 grain"></div>
                <div className="absolute bottom-4 left-4 h-16 w-16 rounded-full bg-white border-4 border-white flex items-center justify-center font-display font-bold text-[#003399] text-2xl shadow-lg">
                  {p.name.charAt(0)}
                </div>
              </div>
              <div className="p-5 pt-8">
                <div className="font-display font-semibold text-[#0B1120]">{p.name}</div>
                <div className="text-xs text-[#003399] font-display mt-1">{p.role}</div>
                <div className="mt-3 text-[10px] tracking-[0.2em] uppercase text-slate-400">{p.country} · {p.years}</div>
                <div className="mt-4 flex items-center gap-2 text-[10px] text-slate-500">
                  <Linkedin size={12} strokeWidth={1.5}/> <span>Profile placeholder</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-24">
          <SectionHeader eyebrow="Expansion" title={t("members.future_title")} subtitle={t("members.future_subtitle")} />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {openPositions.map((country, i) => (
              <div key={country} data-testid={`open-position-${i}`} className="border-2 border-dashed border-slate-300 p-6 rounded-sm text-center hover:border-[#003399] hover:bg-[#003399]/5 transition-all cursor-pointer">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-display">{t("members.open_position")}</div>
                <div className="mt-2 font-display font-semibold text-[#003399]">{country}</div>
                <Link to="/contact#partner" className="mt-4 inline-flex items-center gap-1 text-xs text-[#003399] hover:text-[#00E5FF] transition-colors font-display">
                  {t("members.apply")} <ArrowRight size={12}/>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesGrid = () => {
  const { t } = useTranslation();
  const items = t("services_grid.items", { returnObjects: true });
  return (
    <section className="py-24 md:py-32 bg-[#F5F7FB]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeader eyebrow={t("services_grid.eyebrow")} title={t("services_grid.title")} align="center" />
        <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {items.map((s, i) => (
            <motion.div
              key={s}
              initial={{opacity:0, scale:0.96}}
              whileInView={{opacity:1, scale:1}}
              viewport={{once:true}}
              transition={{delay:i*0.03, duration:0.35}}
              className="bg-white border border-slate-200 p-6 rounded-sm hover:bg-[#003399] hover:text-white hover:border-[#003399] transition-colors group cursor-default"
              data-testid={`service-${i}`}
            >
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 group-hover:text-[#00E5FF] font-display">{String(i+1).padStart(2,'0')}</div>
              <div className="mt-3 font-display font-semibold text-sm">{s}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BrandsSection = () => {
  const { t } = useTranslation();
  const cats = t("brands.categories", { returnObjects: true });
  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeader eyebrow={t("brands.eyebrow")} title={t("brands.title")} subtitle={t("brands.subtitle")} align="center" />
      </div>
      <div className="mt-16">
        <Marquee gradient={false} speed={40}>
          {cats.concat(cats).map((c, i) => (
            <div key={i} className="mx-4 px-8 py-6 border border-slate-200 rounded-sm bg-white flex items-center gap-3 min-w-[260px]">
              <div className="h-10 w-10 rounded-sm bg-slate-100 flex items-center justify-center">
                <Building2 size={18} strokeWidth={1.5} className="text-[#003399]"/>
              </div>
              <span className="font-display font-medium text-sm text-[#0B1120]">{c}</span>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

const MethodSection = () => {
  const { t } = useTranslation();
  const steps = t("method.steps", { returnObjects: true });
  return (
    <section className="py-24 md:py-32 bg-[#0B1120] text-white relative overflow-hidden">
      <div className="absolute -right-40 top-20 h-96 w-96 rounded-full bg-[#003399]/40 blur-3xl"></div>
      <div className="absolute -left-40 bottom-20 h-96 w-96 rounded-full bg-[#00E5FF]/10 blur-3xl"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <SectionHeader eyebrow={t("method.eyebrow")} title={t("method.title")} light />
        <div className="mt-16 space-y-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{opacity:0, x:-30}}
              whileInView={{opacity:1, x:0}}
              viewport={{once:true}}
              transition={{delay:i*0.08}}
              className="grid grid-cols-12 gap-4 md:gap-8 items-center border-b border-white/10 pb-4"
              data-testid={`method-step-${i}`}
            >
              <div className="col-span-2 md:col-span-1 font-display font-bold text-4xl md:text-6xl text-[#00E5FF]">{String(i+1).padStart(2,'0')}</div>
              <div className="col-span-10 md:col-span-4">
                <h3 className="font-display font-semibold text-xl md:text-2xl">{s.name}</h3>
              </div>
              <div className="col-span-12 md:col-span-7 text-white/70 text-sm md:text-base">{s.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const NewsSection = ({ articles }) => {
  const { t } = useTranslation();
  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <SectionHeader eyebrow={t("news.eyebrow")} title={t("news.title")} />
          <Link to="/news" data-testid="news-view-all" className="text-sm font-display font-medium text-[#003399] hover:text-[#00E5FF] transition-colors uppercase tracking-widest inline-flex items-center gap-2">
            {t("news.view_all")} <ArrowRight size={16}/>
          </Link>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {(articles || []).slice(0,3).map((a, i) => (
            <Link key={a.id || i} to={`/news/${a.slug}`} data-testid={`news-card-${i}`} className="premium-card overflow-hidden block group">
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                {a.cover_image && <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>}
              </div>
              <div className="p-6">
                <PillLabel tone="muted" testId={`news-cat-${i}`}>{a.category}</PillLabel>
                <h3 className="mt-4 font-display font-semibold text-lg text-[#0B1120] leading-snug line-clamp-2 group-hover:text-[#003399] transition-colors">{a.title}</h3>
                <p className="mt-2 text-sm text-[#4A5568] line-clamp-2">{a.excerpt}</p>
                <div className="mt-4 text-xs text-slate-400 font-display flex items-center gap-2">
                  <Calendar size={12}/> {new Date(a.created_at).toLocaleDateString()}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTAFinal = () => {
  const { t } = useTranslation();
  return (
    <section id="cta" className="py-24 md:py-40 bg-[#003399] text-white relative overflow-hidden">
      <div className="absolute inset-0 grain"></div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF] to-transparent animate-pulse-line"></div>
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative">
        <div className="eyebrow" style={{color:'#00E5FF'}}>{t("cta_final.eyebrow")}</div>
        <h2 className="mt-6 font-display font-semibold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight">{t("cta_final.title")}</h2>
        <p className="mt-6 text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">{t("cta_final.subtitle")}</p>
        <Link to="/contact#partner" data-testid="cta-final-button" className="btn-primary mt-10 !bg-white !text-[#003399] !border-white hover:!bg-[#00E5FF] hover:!border-[#00E5FF] inline-flex">
          {t("cta_final.button")} <ArrowRight size={16}/>
        </Link>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF] to-transparent animate-pulse-line"></div>
    </section>
  );
};

const Hero = () => {
  const { t } = useTranslation();
  return (
    <section id="hero" className="relative min-h-screen w-full overflow-hidden bg-[#0B1120]">
      <div className="absolute inset-0 animate-kenburns">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover"/>
      </div>
      <div className="absolute inset-0 hero-gradient"></div>
      <div className="absolute inset-0 hero-gradient-bottom"></div>
      <div className="absolute inset-0 grain"></div>
      {/* Corner tick marks */}
      <div className="absolute top-24 left-6 lg:left-8 hidden md:flex items-center gap-3 text-white/60 text-[10px] tracking-[0.3em] uppercase font-display">
        <span className="h-px w-8 bg-[#00E5FF]"></span> N 45.4642° · E 9.1900°
      </div>
      <div className="absolute top-24 right-6 lg:right-8 hidden md:block text-white/60 text-[10px] tracking-[0.3em] uppercase font-display text-right">
        Est. 2024 · European Alliance
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-40 md:pt-48 pb-32">
        <motion.div initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{duration:0.8}}>
          <div className="eyebrow !text-[#00E5FF]" style={{color:'#00E5FF'}}>{t("hero.eyebrow")}</div>
        </motion.div>
        <motion.h1 initial={{opacity:0,y:40}} animate={{opacity:1,y:0}} transition={{duration:0.9, delay:0.15}} className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-5xl">
          <span className="block">{t("hero.title_line1")}</span>
          <span className="block text-[#00E5FF]">{t("hero.title_line2")}</span>
        </motion.h1>
        <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.9, delay:0.35}} className="mt-8 max-w-2xl text-lg md:text-xl text-white/85 leading-relaxed font-light">
          {t("hero.subtitle")}
        </motion.p>
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.9, delay:0.5}} className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/contact#partner" data-testid="hero-cta-primary" className="btn-primary !bg-white !text-[#003399] !border-white hover:!bg-[#00E5FF] hover:!border-[#00E5FF]">
            {t("hero.cta_primary")} <ArrowRight size={16}/>
          </Link>
          <a href="#why-eema" data-testid="hero-cta-secondary" className="btn-ghost-white">
            {t("hero.cta_secondary")}
          </a>
        </motion.div>

        <div className="mt-24 md:mt-32 flex items-center gap-3 text-white/50 text-[10px] tracking-[0.3em] uppercase font-display">
          <ArrowDown size={14} strokeWidth={1.5}/> {t("hero.scroll")}
        </div>
      </div>

      {/* Bottom stats bar */}
      <div className="relative z-10 border-t border-white/10 bg-black/30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            {v:"6", l:"Founding markets"},
            {v:"15", l:"Target countries"},
            {v:"250+", l:"Dealer network"},
            {v:"50+", l:"Years combined experience"},
          ].map((s, i) => (
            <div key={i} className="text-white">
              <div className="font-display font-bold text-2xl md:text-3xl text-white">{s.v}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

import { useEffect } from "react";
import { apiClient } from "../lib/api";

export default function Home() {
  const [articles, setArticles] = React.useState([]);
  useEffect(() => {
    apiClient.get("/articles?limit=3").then(r => setArticles(r.data)).catch(() => setArticles([]));
  }, []);
  return (
    <div data-testid="page-home">
      <Hero />
      <WhyCards />
      <MarketsSection />
      <TimelineSection />
      <GoToMarketSection />
      <CapabilitiesSection />
      <CompareSection />
      <MembersSection />
      <ServicesGrid />
      <BrandsSection />
      <MethodSection />
      <NewsSection articles={articles} />
      <CTAFinal />
    </div>
  );
}
